import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X } from 'lucide-react';
import { Button } from './ui/button';

export default function InstallPWA() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      // Prevent Chrome 67+ from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      setInstallPrompt(e);
      // Show custom install prompt after a delay
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowPrompt(false);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;

    // Show the install prompt
    installPrompt.prompt();

    // Wait for user response
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('PWA installed');
    }

    // Clear the prompt
    setInstallPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Don't show again for this session
    sessionStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  // Don't show if dismissed this session
  useEffect(() => {
    if (sessionStorage.getItem('pwa-prompt-dismissed')) {
      setShowPrompt(false);
    }
  }, []);

  return (
    <AnimatePresence>
      {showPrompt && installPrompt && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className="fixed bottom-20 left-4 right-4 md:left-auto md:right-6 md:w-80 z-50"
          data-testid="pwa-install-prompt"
        >
          <div className="glass-card p-4 border border-cosmic-accent-gold/30">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-cosmic-accent-gold/20 flex items-center justify-center flex-shrink-0">
                <Download className="w-5 h-5 text-cosmic-accent-gold" />
              </div>
              <div className="flex-1">
                <h3 className="text-cosmic-text-primary font-medium mb-1">
                  Install ChakraFlow
                </h3>
                <p className="text-cosmic-text-muted text-sm mb-3">
                  Add to your home screen for quick access and offline support.
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={handleInstall}
                    className="btn-gold text-sm py-2 px-4"
                    data-testid="pwa-install-btn"
                  >
                    Install
                  </Button>
                  <Button
                    onClick={handleDismiss}
                    variant="ghost"
                    className="text-cosmic-text-muted text-sm py-2 px-4"
                  >
                    Not now
                  </Button>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="text-cosmic-text-muted hover:text-cosmic-text-primary"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
